import { FormInstance } from 'antd';
import axios, { AxiosResponse } from 'axios';
import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

import { CONSTANTS } from '../AuthModal.data';
import { AuthViewT, LoginDtoT } from '../AuthModal.types';
import { getServerErrorMessage } from '../AuthModal.utils';

export type AuthContextT = {
  children: ReactNode;

  apiBaseUrl: string;

  isLoginOnly?: boolean;
  isAuthCloseDisabled?: boolean;

  isLoading?: boolean;
  setIsLoading?: (loading: boolean) => void;
  isLoadingResend?: boolean;
  setIsLoadingResend?: (loading: boolean) => void;

  serverError?: string | undefined | null;
  setServerError?: (message: string | null) => void;

  // events
  onLogin: (token: string) => void;
  onClose: () => void;

  // handlers
  handleLookup?: (
    form: FormInstance<any>,
    onChangeView?: (view?: AuthViewT) => void,
    defaultErrorLabel?: string
  ) => any;
  handleSendValidationCode?: (
    email: string,
    onChangeView?: (view?: AuthViewT) => void,
    defaultErrorLabel?: string
  ) => any;

  isLoadingView?: boolean;
  setIsLoadingView?: (loading: boolean) => void;

  isAuthCloseTemporarilyDisabled?: boolean;
  setIsAuthCloseTemporarilyDisabled?: (disabled: boolean) => void;

  // api
  apiLogin?: (user: LoginDtoT) => Promise<AxiosResponse>;
  apiLookup?: (email: string) => Promise<AxiosResponse>;
  apiSendValidationCode?: (email: string) => Promise<AxiosResponse>;
  apiValidate?: (email: string, code: string) => Promise<AxiosResponse>;
  apiSignup?: (user: LoginDtoT) => Promise<AxiosResponse>;
  apiSendResetToken?: (email: string) => Promise<AxiosResponse>;

  isLightTheme?: boolean;

  invitationToken?: string;
  prefilledEmail?: string;
};

const AuthContext = createContext<AuthContextT | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used within an AuthContextProvider');
  }

  return context;
};

export const AuthContextProvider = ({ ...props }: AuthContextT) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingResend, setIsLoadingResend] = useState(false);
  const [serverError, setServerError] = useState<string | undefined | null>();
  const [isLoginOnly, setIsLoginOnly] = useState(props.isLoginOnly);

  const [isLoadingView, setIsLoadingView] = useState(false);
  const [isAuthCloseTemporarilyDisabled, setIsAuthCloseTemporarilyDisabled] = useState(false);

  const theme = useTheme();

  const isLightTheme = useMemo(
    () => theme.Color.Neutrals.Dark === theme.Color.NeutralsFixed.Dark,
    [theme.Color.Neutrals.Dark, theme.Color.NeutralsFixed.Dark]
  );

  const apiLogin = async (user: LoginDtoT) => {
    return await axios.post(`${props.apiBaseUrl}/api/login`, user);
  };

  const apiLookup = useCallback(
    async (email: string) => {
      return await axios.post(`${props.apiBaseUrl}/api/lookup`, { email });
    },
    [props.apiBaseUrl]
  );

  const apiSendValidationCode = useCallback(
    async (email: string) => {
      return await axios.post(`${props.apiBaseUrl}/api/email/send-validation-code`, {
        email,
      });
    },
    [props.apiBaseUrl]
  );

  const apiValidate = async (email: string, code: string) => {
    return await axios.post(`${props.apiBaseUrl}/api/email/validate`, {
      email,
      code,
    });
  };

  const apiSignup = async (user: LoginDtoT) => {
    return await axios.post(`${props.apiBaseUrl}/api/signup`, user);
  };

  const apiSendResetToken = async (email: string) => {
    return await axios.post(`${props.apiBaseUrl}/api/password/send-reset-token`, {
      email,
    });
  };

  const handleSendValidationCode = useCallback(
    async (
      email: string,
      onChangeView?: (view?: AuthViewT) => void,
      defaultErrorLabel?: string
    ) => {
      setIsLoadingResend?.(true);
      setServerError?.(null);

      await apiSendValidationCode(email)
        .then((res: AxiosResponse) => {
          console.debug('Validation code sent', res);
          // onChangeView is possibly undefined as vuew may and may not change depending on whether the view change is triggered from handleLookup (changed) or resend button (not changed)
          onChangeView?.('signupCode');
        })
        .catch((err: any) => {
          console.error('Error sending validation code', err.message);

          const serverErrorMessage = getServerErrorMessage(err);

          setServerError?.(serverErrorMessage);
        })
        .finally(() => {
          setIsLoadingResend?.(false);
        });
    },
    [apiSendValidationCode]
  );

  const handleLookup = useCallback(
    async (
      form: FormInstance<any>,
      onChangeView?: (view?: AuthViewT) => void,
      defaultErrorLabel?: string
    ) => {
      if (isLoading) return;

      await form
        .validateFields()

        .then(async () => {
          const email = form.getFieldValue(CONSTANTS.form.emailFieldName);

          setIsLoading?.(true);
          setServerError?.(null);

          await apiLookup(email)
            .then((res: AxiosResponse) => {
              // 226 - user exists, continue to password
              onChangeView?.('loginPassword');
            })

            .catch(async (err) => {
              const { status } = err.response;

              // 404 - switch to signup
              if (status === 404) {
                // isLoginOnly - show error under the input field
                if (isLoginOnly) {
                  const serverErrorMessage = getServerErrorMessage(err) ?? defaultErrorLabel;

                  form.setFields([
                    {
                      name: CONSTANTS.form.emailFieldName,
                      errors: [serverErrorMessage],
                    },
                  ]);

                  // don't continue to send validation code
                  return;
                }

                // else - send validation code
                await handleSendValidationCode(email, onChangeView, defaultErrorLabel);

                // 5XX - server error
              } else if (status >= 500) {
                setServerError?.(defaultErrorLabel);
              }
            })
            .finally(() => {
              setIsLoading?.(false);
            });
        })
        .catch((error: any) => {
          console.error('Form validation failed.', error);
          return;
        });
    },
    [apiLookup, handleSendValidationCode, isLoading, isLoginOnly]
  );

  return (
    <AuthContext.Provider
      value={{
        ...props,
        isLoading,
        setIsLoading,
        isLoadingResend,
        setIsLoadingResend,
        serverError,
        setServerError,
        handleLookup,
        handleSendValidationCode,
        apiLogin,
        apiLookup,
        apiSendValidationCode,
        apiValidate,
        apiSignup,
        apiSendResetToken,
        isLoadingView,
        setIsLoadingView,
        isAuthCloseTemporarilyDisabled,
        setIsAuthCloseTemporarilyDisabled,
        isLightTheme,
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};
